import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { showToast } from '../../utils/toast';
import { createThread } from '../../services/boardService';
import { CATEGORIES } from '../../data/boardCategories';

const MAX_TITLE = 100;
const MAX_CONTENT = 240;

const THREAD_TEMPLATES = {
  'mountain-report': {
    label: '山行報告',
    title: '【山行報告】山名を入れてください',
    content: `行った山:\nコース:\n服装・装備:\n危なかった点:\nおすすめポイント:\n質問歓迎: はい / いいえ`,
    isQA: false,
    helper: '報告を日記で終わらせず、返信しやすい項目を先に埋めるテンプレです。',
  },
  'gear-consult': {
    label: '装備相談',
    title: '【装備相談】何を選ぶか相談したいです',
    content: `登山歴:\n行きたい山:\n予算:\n迷っている候補:\n不安な点:\nいつまでに決めたいか:`,
    isQA: true,
    helper: '靴・レイヤリング・ザックなどの相談を続きやすくするテンプレです。',
  },
  'beginner-question': {
    label: '初心者質問',
    title: '【初心者質問】初歩的な内容でも大丈夫です',
    content: `いま困っていること:\n登山歴:\n行く予定の山:\n手元にある装備:\n特に不安な点:`,
    isQA: true,
    helper: '返信する側が状況を把握しやすいように、最低限の前提をそろえます。',
  },
  'weekend-plan': {
    label: '今週末どこ行く？',
    title: '【今週末どこ行く？】予定・天気・装備を共有しよう',
    content: `行く予定の山:\n気にしている天気:\n同行者の有無:\n装備で迷っていること:\n帰ってきたら報告する予定: はい / いいえ`,
    isQA: false,
    helper: '週末前に見たくなる定例スレを立てるためのテンプレです。',
  },
};

function BoardNewThreadForm({ initialCategoryId = '', initialSubCategoryId = '', initialTemplate = '' }) {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  const normalizedTemplate = initialTemplate in THREAD_TEMPLATES ? initialTemplate : '';
  const initialTemplateData = normalizedTemplate ? THREAD_TEMPLATES[normalizedTemplate] : null;

  const [title, setTitle] = useState(initialTemplateData?.title ?? '');
  const [content, setContent] = useState(initialTemplateData?.content ?? '');
  const [categoryId, setCategoryId] = useState(initialCategoryId);
  const [subCategoryId, setSubCategoryId] = useState(initialSubCategoryId);
  const [isQA, setIsQA] = useState(initialTemplateData?.isQA ?? false);
  const [selectedTemplate, setSelectedTemplate] = useState(normalizedTemplate);
  const [loading, setLoading] = useState(false);

  const selectedCategory = CATEGORIES.find((c) => c.id === categoryId);

  const applyTemplate = (key) => {
    setSelectedTemplate(key);
    if (!key) return;
    const t = THREAD_TEMPLATES[key];
    setTitle(t.title);
    setContent(t.content);
    setIsQA(t.isQA);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!categoryId || !subCategoryId) {
      showToast({ message: 'カテゴリを選択してください', type: 'error' });
      return;
    }
    setLoading(true);
    try {
      const threadId = await createThread({
        title,
        content,
        categoryId,
        subCategoryId,
        isQA,
        authorId: currentUser.uid,
        authorName: currentUser.displayName || '匿名ユーザー',
      });
      showToast({ message: 'スレッドを投稿しました', type: 'success' });
      navigate(`/board/${categoryId}/${subCategoryId}/${threadId}`);
    } catch (err) {
      showToast({ message: `投稿に失敗しました: ${err.message || 'エラー'}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%',
    border: '1px solid #d1d5db',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    boxSizing: 'border-box',
    backgroundColor: 'var(--ch-input-bg)',
    color: 'var(--ch-text)',
  };

  return (
    <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
      {/* テンプレート選択 */}
      <div className="consult-intro-card" style={{ borderRadius: '12px', padding: '16px' }}>
        <p style={{ fontSize: '14px', fontWeight: 600, color: '#065f46', margin: 0 }}>
          会話が続きやすいテンプレート
        </p>
        <p style={{ fontSize: '12px', color: '#047857', marginTop: '4px' }}>
          相談や山行報告は、最初に必要な前提を並べると返信率が上がります。
        </p>
        <div style={{ marginTop: '12px', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
          {Object.keys(THREAD_TEMPLATES).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => applyTemplate(key)}
              style={{
                borderRadius: '9999px',
                border: selectedTemplate === key ? '1px solid #047857' : '1px solid #6ee7b7',
                background: selectedTemplate === key ? '#047857' : '#ffffff',
                color: selectedTemplate === key ? '#ffffff' : '#065f46',
                padding: '2px 12px',
                fontSize: '12px',
                cursor: 'pointer',
              }}
            >
              {THREAD_TEMPLATES[key].label}
            </button>
          ))}
        </div>
        {selectedTemplate && (
          <p style={{ marginTop: '12px', fontSize: '12px', color: '#065f46' }}>
            {THREAD_TEMPLATES[selectedTemplate].helper}
          </p>
        )}
      </div>

      {/* タイトル */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--ch-text)', marginBottom: '4px' }}>
          タイトル <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={MAX_TITLE}
          required
          placeholder="タイトルを入力（100文字以内）"
          style={inputStyle}
        />
        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', textAlign: 'right' }}>
          {title.length}/{MAX_TITLE}
        </p>
      </div>

      {/* 本文 */}
      <div>
        <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--ch-text)', marginBottom: '4px' }}>
          本文 <span style={{ color: '#ef4444' }}>*</span>
        </label>
        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value)}
          maxLength={MAX_CONTENT}
          required
          rows={5}
          placeholder="本文を入力（240文字以内）"
          style={{ ...inputStyle, resize: 'none' }}
        />
        <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', textAlign: 'right' }}>
          {content.length}/{MAX_CONTENT}
        </p>
      </div>

      {/* カテゴリ選択 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--ch-text)', marginBottom: '4px' }}>
            カテゴリ <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            value={categoryId}
            onChange={(e) => { setCategoryId(e.target.value); setSubCategoryId(''); }}
            required
            style={inputStyle}
          >
            <option value="">選択してください</option>
            {CATEGORIES.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--ch-text)', marginBottom: '4px' }}>
            サブカテゴリ <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <select
            value={subCategoryId}
            onChange={(e) => setSubCategoryId(e.target.value)}
            required
            disabled={!selectedCategory}
            style={{ ...inputStyle, opacity: !selectedCategory ? 0.5 : 1 }}
          >
            <option value="">選択してください</option>
            {selectedCategory?.subCategories.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* QAタグ */}
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={isQA}
          onChange={(e) => setIsQA(e.target.checked)}
          style={{ width: '16px', height: '16px' }}
        />
        <span style={{ fontSize: '14px', color: 'var(--ch-text)' }}>「教えて！」タグをつける</span>
      </label>

      <div style={{ borderRadius: '8px', border: '1px solid #e5e7eb', backgroundColor: '#f9fafb', padding: '12px 16px', fontSize: '12px', color: '#6b7280' }}>
        短文でも投稿できます。画像なしでも問題ありません。迷ったらテンプレートを入れてから削る形で十分です。
      </div>

      <button
        type="submit"
        disabled={loading}
        style={{
          width: '100%',
          backgroundColor: loading ? '#9ca3af' : '#16a34a',
          color: '#ffffff',
          padding: '10px',
          borderRadius: '6px',
          fontWeight: 500,
          fontSize: '15px',
          cursor: loading ? 'not-allowed' : 'pointer',
          border: 'none',
        }}
      >
        {loading ? '投稿中...' : '投稿する'}
      </button>
    </form>
  );
}

export default BoardNewThreadForm;
